import { useCallback, useEffect, useMemo, useState } from 'react';
import { User, Truck, Store, Phone, PhoneCall, ExternalLink, Check, Heart, MessageSquareWarning, Package, Gift } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { SECTION_COLOR, SECTION_BG, CERVENA, ORANZOVA, ZELENA, KANALY, hlavicky, fmtKc, fmtDen, platbaPopis } from './ui';

/**
 * Objednávky → Reklamace (2026-10-25). Norma §1: seznam · detail · panel.
 *
 * Tři druhy zpětné vazby: něco chybí, stížnost, pochvala. Přichází ze stránky
 * objednávky (host), z průvodce hovorem (obsluha), od rozvozce a z výdeje.
 * Náhradu (dovézt / peníze / body) má jen „něco chybí" a rozhoduje o ní
 * vedení tady — pokladna jen doveze nebo předá dál. Odpovědět jde u všech:
 * urgentní SMS (z telefonu bistra), běžná e-mailem.
 */
const DRUHY = {
    chybi: { nazev: 'Něco chybí', Icon: Package, barva: '#B45309', pozadi: '#FFF7ED' },
    stiznost: { nazev: 'Stížnost', Icon: MessageSquareWarning, barva: '#B91C1C', pozadi: '#FEF2F2' },
    pochvala: { nazev: 'Pochvala', Icon: Heart, barva: '#047857', pozadi: '#ECFDF5' },
};
const ZDROJE = { zakaznik: { nazev: 'Host', Icon: User }, obsluha: { nazev: 'Hovor', Icon: PhoneCall }, rozvozce: { nazev: 'Rozvozce', Icon: Truck }, vydej: { nazev: 'Výdej', Icon: Store } };
const OBLASTI = { jidlo: 'Jídlo', teplota: 'Teplota', cas: 'Čas doručení', rozvozce: 'Rozvozce', baleni: 'Balení', jine: 'Jiné' };
const PRANI = { dovezt: 'dovézt', penize: 'vrátit peníze', body: 'připsat body', donest: 'donést' };
const STAVY = {
    nove: { nazev: 'Nová', barva: CERVENA },
    dovezt: { nazev: 'Dovezeno', barva: ZELENA },
    doneseno: { nazev: 'Doneseno', barva: ZELENA },
    penize: { nazev: 'Vrátit peníze', barva: ZELENA },
    body: { nazev: 'Body', barva: ZELENA },
    bez_nahrady: { nazev: 'Bez náhrady', barva: null },
    uzavreno: { nazev: 'Uzavřeno', barva: null },
};
const AKCE_CHYBI = [
    { id: 'dovezt', nazev: 'Dovézt', jenSPolozkami: true },
    { id: 'body', nazev: 'Body' },
    { id: 'penize', nazev: 'Vrátit peníze' },
    { id: 'bez_nahrady', nazev: 'Bez náhrady' },
];
// Vyjádření pobočky jedním ťuknutím na pokladně / kuchyni (2026-10-29).
const VYJADRENI = {
    chybelo: { nazev: 'Pobočka: opravdu chybělo', barva: ORANZOVA },
    nechybelo: { nazev: 'Pobočka: nechybělo', barva: '#2563EB' },
    vzato: { nazev: 'Pobočka vzala na vědomí', barva: null },
    prijato: { nazev: 'Pobočka přijala', barva: ZELENA },
};
const POHLEDY = [
    { id: 'k-vyrizeni', label: 'K vyřízení', upozorneni: true, patri: h => h.k_vyrizeni },
    { id: 'vse', label: 'Vše', patri: () => true },
    { id: 'chybi', label: 'Něco chybí', patri: h => h.druh === 'chybi' },
    { id: 'stiznost', label: 'Stížnosti', patri: h => h.druh === 'stiznost' },
    { id: 'pochvala', label: 'Pochvaly', patri: h => h.druh === 'pochvala' },
];

const cas = (iso) => (iso ? new Date(iso).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : '');
const kdy = (iso) => (iso ? `${fmtDen(iso)} ${cas(iso)}` : '—');
const coChybi = (h) => (h.polozky || []).map(p => `${p.pocet}× ${p.nazev}`).join(', ');
const krestni = (jmeno) => (jmeno || '').trim().split(/\s+/)[0] || '';

/** Urgentní (SMS): něco chybí, nebo stížnost do 2 h od doručení. Jinak e-mail, když ho host má. */
function vychoziKanal(h) {
    const email = h.objednavka?.zakaznik?.email;
    const telefon = h.objednavka?.zakaznik?.telefon;
    if (!email) return 'sms';
    if (!telefon) return 'email';
    if (h.kanal_hosta) return h.kanal_hosta;
    if (h.druh === 'pochvala') return 'email';
    if (h.druh === 'chybi') return 'sms';
    const od = h.objednavka?.doruceno ? new Date(h.objednavka.doruceno).getTime() : 0;
    return Date.now() - od < 2 * 3600000 ? 'sms' : 'email';
}

/** Délka SMS — GSM 160 / 153 na část, s diakritikou (UCS-2) 70 / 67. */
function smsDelka(text) {
    const ucs = /[^\x20-\x7E\n\r€£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉÄÖÑÜ§¿äöñüà]/.test(text);
    const [jedna, dil] = ucs ? [70, 67] : [160, 153];
    const n = text.length;
    return { znaku: n, casti: n <= jedna ? 1 : Math.ceil(n / dil), ucs };
}

function stavHlaseni(h) {
    if (h.k_provedeni) return { nazev: h.stav === 'penize' ? 'Vrátit peníze' : 'Připsat body', barva: ORANZOVA };
    return STAVY[h.stav] || { nazev: h.stav, barva: null };
}

export default function Reklamace({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, muzeUpravit = false, onObjednavka, onZakaznik }) {
    const [data, setData] = useState(null);
    const [pohled, setPohled] = useState('k-vyrizeni');
    const [hledani, setHledani] = useState('');
    const [vybranoId, setVybranoId] = useState(null);
    const [castka, setCastka] = useState('');
    const [poznamka, setPoznamka] = useState('');
    const [reseni, setReseni] = useState(null);
    const [kanal, setKanal] = useState('sms');
    // Text odpovědi píše vždy vedení samo (2026-10-25) — žádná šablona,
    // nic ho nepřepisuje. Vymaže se jen po odeslání nebo při jiné reklamaci.
    const [odpoved, setOdpoved] = useState('');
    const [bezi, setBezi] = useState(null);
    const [body, setBody] = useState('');
    const [chyba, setChyba] = useState(null);
    const muted = mutedColor;
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle, rgba(0,0,0,0.06))';

    const nacti = useCallback(() => {
        fetch('/api/objednavky/hlaseni', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setData(d.polozky); }).catch(() => {});
    }, []);
    useEffect(() => { nacti(); }, [nacti]);
    // Pokladna i hosté přidávají souběžně — seznam se drží čerstvý.
    useEffect(() => { const t = setInterval(nacti, 60000); return () => clearInterval(t); }, [nacti]);

    const polozky = data || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, polozky.filter(p.patri).length])), [polozky]);
    const aktivni = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const hledat = hledani.trim().toLowerCase();
    const seznam = polozky.filter(h => aktivni.patri(h) && (!hledat || [
        h.objednavka?.cislo, h.objednavka?.zakaznik?.jmeno, h.objednavka?.zakaznik?.telefon, h.objednavka?.adresa, h.poznamka, coChybi(h),
    ].join(' ').toLowerCase().includes(hledat)));
    const vybrane = polozky.find(h => h.id === vybranoId) || null;

    // Po výběru (a po uložení, i z pokladny) se pole srovnají se serverem.
    useEffect(() => {
        setChyba(null);
        if (!vybrane) return;
        setCastka(vybrane.stav !== 'nove' ? String(Math.round(vybrane.castka)) : '');
        setPoznamka(vybrane.vyrizeni_poznamka || '');
        const r = vybrane.stav !== 'nove' ? vybrane.stav : vybrane.druh === 'chybi' ? (vybrane.prani && vybrane.prani !== 'donest' ? vybrane.prani : 'body') : null;
        setReseni(r);
    }, [vybranoId, vybrane?.stav, vybrane?.castka, vybrane?.vyrizeni_poznamka]); // eslint-disable-line react-hooks/exhaustive-deps

    // Kanál a text odpovědi jen při přepnutí na jinou reklamaci — uložení
    // částky, poznámky ani obnova seznamu do rozepsané odpovědi nesahají.
    useEffect(() => {
        setOdpoved('');
        setBody('');
        if (vybrane) setKanal(vychoziKanal(vybrane));
    }, [vybranoId]); // eslint-disable-line react-hooks/exhaustive-deps

    const castkaCislo = castka.trim() === '' ? null : Number(castka.replace(',', '.'));

    const poslat = async (klic, url, metoda, telo) => {
        setBezi(klic);
        setChyba(null);
        try {
            const res = await fetch(url, { method: metoda, headers: hlavicky(), body: JSON.stringify(telo) });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(d.error || d.message || 'Nepodařilo se uložit.');
            setData(d.polozky);
            return true;
        } catch (e) {
            setChyba(e.message);
            return false;
        } finally {
            setBezi(null);
        }
    };
    const vyresit = (akce) => poslat(akce, `/api/objednavky/hlaseni/${vybrane.id}/vyresit`, 'POST', {
        akce,
        castka: ['bez_nahrady', 'uzavrit'].includes(akce) ? null : castkaCislo,
        poznamka: poznamka.trim() || null,
    });
    const odpovedet = async (uzavrit) => {
        if (await poslat(uzavrit ? 'odeslat-uzavrit' : 'odeslat', `/api/objednavky/hlaseni/${vybrane.id}/odpovedet`, 'POST', { kanal, text: odpoved, uzavrit })) setOdpoved('');
    };
    const upravit = (klic, telo) => poslat(klic, `/api/objednavky/hlaseni/${vybrane.id}`, 'PATCH', telo);
    // Něco chybí: rozhodnout a rovnou odpovědět jedním ťuknutím.
    const vyresitAOdpovedet = async () => {
        const text = odpoved;
        if (!(await vyresit(reseni))) return;
        if (text.trim() && await poslat('odeslat', `/api/objednavky/hlaseni/${vybrane.id}/odpovedet`, 'POST', { kanal, text })) setOdpoved('');
    };

    const chip = (h) => {
        const s = stavHlaseni(h);
        return (
            <span className="shrink-0 text-[11px] font-semibold rounded-full" style={{ padding: '3px 9px', color: s.barva || muted, background: s.barva ? `${s.barva}1F` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)') }}>
                {s.nazev}
            </span>
        );
    };
    const druhIkona = (h, velikost = 34) => {
        const d = DRUHY[h.druh] || DRUHY.chybi;
        return (
            <span style={{ width: velikost, height: velikost, borderRadius: 9, background: isDark ? `${d.barva}33` : d.pozadi, color: isDark ? '#fff' : d.barva, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <d.Icon size={Math.round(velikost / 2)} />
            </span>
        );
    };

    const radek = (h, siroky) => {
        const vyb = h.id === vybranoId;
        const o = h.objednavka;
        return (
            <button key={h.id} type="button" onClick={() => setVybranoId(h.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 60, background: vyb ? SECTION_BG : 'transparent', boxShadow: vyb ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                {druhIkona(h)}
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>
                        #{o?.cislo ?? '—'}{o?.zakaznik?.jmeno ? ` · ${o.zakaznik.jmeno}` : ''}
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', color: muted, fontSize: isMobile ? 13 : 12 }}>
                        {h.vyjadreni && VYJADRENI[h.vyjadreni] && <b style={{ color: VYJADRENI[h.vyjadreni].barva || muted, fontWeight: 600 }}>{VYJADRENI[h.vyjadreni].nazev.replace('Pobočka: ', '').replace('Pobočka ', '')} · </b>}
                        {h.oblast ? `${OBLASTI[h.oblast] ?? h.oblast} · ` : ''}{coChybi(h) || h.poznamka || 'Bez popisu'}
                    </p>
                </div>
                {siroky && <span className="shrink-0 text-xs" style={{ width: 80, color: muted }}>{ZDROJE[h.zdroj]?.nazev ?? h.zdroj}</span>}
                {siroky && <span className="shrink-0 text-xs tabular-nums" style={{ width: 110, color: muted }}>{kdy(h.vytvoreno_v)}</span>}
                {chip(h)}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat číslo, hosta, telefon…', placeholderKratky: 'Hledat' }}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? SECTION_COLOR : undefined })) : null}
                pohledAktivni={aktivni.id}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={aktivni.id === p.id}
                        upozorneni={p.upozorneni && pocty[p.id] > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje' : aktivni.id === 'k-vyrizeni' ? 'Nic nečeká na vyřízení.' : 'Za posledních 90 dní tu nic není.'}
                    </p>
                )}
                {seznam.map(h => radek(h, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const nadpis = (text) => (
        <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '18px 0 6px' }}>{text}</p>
    );
    const pole = (label, hodnota) => (hodnota ? (
        <div className="flex justify-between gap-4 text-[13.5px]" style={{ minHeight: 34, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
            <span style={{ color: muted }}>{label}</span><span className="text-right" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{hodnota}</span>
        </div>
    ) : null);
    const okraj = isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)';
    const tlacitko = (klic, nazev, onClick, { primarni = false, disabled = false } = {}) => (
        <button key={klic} type="button" onClick={onClick} disabled={!!bezi || disabled} className="rounded-lg font-semibold"
            style={{ height: isMobile ? 46 : 38, padding: '0 16px', fontSize: 14, cursor: bezi ? 'wait' : disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, flex: isMobile ? 1 : undefined,
                background: primarni ? SECTION_COLOR : 'transparent', color: primarni ? '#fff' : 'var(--text-primary)', border: primarni ? 'none' : `1px solid ${okraj}` }}>
            {bezi === klic ? '…' : nazev}
        </button>
    );
    const volba = (aktivniVolba, nazev, onClick, disabled = false) => (
        <button type="button" onClick={onClick} disabled={disabled} className="rounded-lg font-semibold"
            style={{ height: 40, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
                background: aktivniVolba ? `${SECTION_COLOR}1F` : 'transparent', color: aktivniVolba ? SECTION_COLOR : 'var(--text-primary)',
                border: aktivniVolba ? `2px solid ${SECTION_COLOR}` : `1px solid ${okraj}` }}>
            {nazev}
        </button>
    );

    const blokOdpovedi = (h, { sOdeslanim }) => {
        const z = h.objednavka?.zakaznik;
        const kam = kanal === 'sms' ? z?.telefon : z?.email;
        const sms = smsDelka(odpoved);
        return (
            <div style={{ marginTop: 14, border: `1px solid ${okraj}`, borderRadius: 12, padding: 14 }}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <b className="text-sm" style={{ color: 'var(--text-primary)' }}>Odpověď hostovi</b>
                    <div className="flex rounded-lg" role="group" aria-label="Naléhavost a kanál" style={{ padding: 3, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
                        {[['sms', 'Urgentní · SMS', z?.telefon], ['email', 'Běžná · e-mail', z?.email]].map(([id, nazev, ma]) => (
                            <button key={id} type="button" onClick={() => setKanal(id)} disabled={!ma} title={ma ? undefined : (id === 'sms' ? 'Host nemá telefon' : 'Host nemá e-mail')}
                                className="rounded-md text-[13px] font-semibold"
                                style={{ padding: '6px 12px', border: 'none', cursor: ma ? 'pointer' : 'not-allowed', opacity: ma ? 1 : 0.4,
                                    background: kanal === id ? (isDark ? '#fff' : '#18181B') : 'transparent', color: kanal === id ? (isDark ? '#18181B' : '#fff') : 'var(--text-primary)' }}>
                                {nazev}
                            </button>
                        ))}
                    </div>
                </div>
                <textarea value={odpoved} onChange={e => setOdpoved(e.target.value)} rows={4} aria-label="Text odpovědi"
                    placeholder={`Napište odpověď hostovi${krestni(z?.jmeno) ? ` (${krestni(z?.jmeno)})` : ''} — odejde přesně tak, jak ji napíšete.`}
                    style={{ ...inputStyle, height: 'auto', padding: '10px 12px', fontSize: 14.5, lineHeight: 1.5, marginTop: 10, resize: 'vertical' }} />
                <p className="text-xs" style={{ color: muted, margin: '6px 0 0' }}>
                    {kanal === 'sms'
                        ? `SMS na ${kam || '—'} · ${sms.znaku} znaků, ${sms.casti} ${sms.casti === 1 ? 'zpráva' : sms.casti < 5 ? 'zprávy' : 'zpráv'}${sms.ucs ? ' (s diakritikou)' : ''} · z telefonu bistra`
                        : `E-mail na ${kam || '—'} · odesílá neodpovidat@fofrlama.cz`}
                </p>
                {sOdeslanim && (
                    <div className="flex flex-wrap gap-2 justify-end" style={{ marginTop: 10 }}>
                        {h.stav === 'nove' && tlacitko('uzavrit', 'Uzavřít bez odpovědi', () => vyresit('uzavrit'))}
                        {h.stav === 'nove'
                            ? tlacitko('odeslat-uzavrit', 'Odeslat a uzavřít', () => odpovedet(true), { primarni: true, disabled: !kam || !odpoved.trim() })
                            : tlacitko('odeslat', 'Odeslat', () => odpovedet(false), { primarni: true, disabled: !kam || !odpoved.trim() })}
                    </div>
                )}
            </div>
        );
    };

    const detailPanel = () => {
        const h = vybrane;
        const o = h?.objednavka;
        const d = h ? (DRUHY[h.druh] || DRUHY.chybi) : null;
        const lzeDovezt = (h?.polozky || []).length > 0 && o?.kanal === 'rozvoz';
        const chybiNove = h?.druh === 'chybi' && h.stav === 'nove';
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '10px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranoId(null)} popis="Zpět na reklamace" sklo isDark={isDark} style={{ marginBottom: 10 }} />}
                    {h ? (
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-bold rounded-full" style={{ padding: '4px 10px', background: isDark ? `${d.barva}33` : d.pozadi, color: isDark ? '#fff' : d.barva }}>
                                        {d.nazev}{h.oblast ? ` · ${OBLASTI[h.oblast] ?? h.oblast}` : ''}
                                    </span>
                                    <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, color: 'var(--text-primary)', margin: 0 }}>
                                        #{o?.cislo ?? '—'}{o?.zakaznik?.jmeno ? ` · ${o.zakaznik.jmeno}` : ''}
                                    </h2>
                                </div>
                                <p className="text-xs" style={{ color: muted, margin: '6px 0 0' }}>
                                    {ZDROJE[h.zdroj]?.nazev ?? h.zdroj} · {kdy(h.vytvoreno_v)}{h.prani && PRANI[h.prani] ? ` · přeje si ${PRANI[h.prani]}` : ''}{h.kanal_hosta ? ` · odpověď chce ${h.kanal_hosta === 'email' ? 'e-mailem' : 'SMS'}` : ''}
                                </p>
                            </div>
                            {chip(h)}
                        </div>
                    ) : <p className="text-sm" style={{ color: muted }}>Reklamace už v seznamu není.</p>}
                </div>
                {h && (
                    <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                        {coChybi(h) && <p className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>Chybí: {coChybi(h)}</p>}
                        {h.poznamka && (
                            <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary)', margin: coChybi(h) ? '8px 0 0' : 0, lineHeight: 1.55, padding: '10px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                                „{h.poznamka}"
                            </p>
                        )}

                        {nadpis('Objednávka')}
                        {pole('Kdy', kdy(o?.vytvoreno))}
                        {pole('Kanál', KANALY[o?.kanal] ?? o?.kanal)}
                        {pole('Adresa', o?.adresa)}
                        {pole('Celkem', o ? `${fmtKc(o.celkem)} · ${platbaPopis(o.typ_platby)}${o.zaplaceno ? '' : ' (nezaplaceno)'}` : null)}
                        {o?.zakaznik?.telefon && (
                            <div className="flex justify-between gap-4 text-[13.5px]" style={{ minHeight: 34, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                                <span style={{ color: muted }}>Telefon</span>
                                <a href={`tel:${o.zakaznik.telefon.replace(/\s+/g, '')}`} className="font-semibold flex items-center gap-1.5" style={{ color: SECTION_COLOR }}>
                                    <Phone size={13} />{o.zakaznik.telefon}
                                </a>
                            </div>
                        )}
                        {pole('E-mail', o?.zakaznik?.email)}
                        <div className="flex flex-wrap gap-3" style={{ marginTop: 10 }}>
                            {onObjednavka && o && (
                                <button type="button" onClick={() => onObjednavka(o.id)} className="text-xs font-semibold flex items-center gap-1" style={{ background: 'none', border: 'none', padding: 0, color: SECTION_COLOR, cursor: 'pointer' }}>
                                    <ExternalLink size={12} />Otevřít objednávku
                                </button>
                            )}
                            {onZakaznik && o?.zakaznik?.id && (
                                <button type="button" onClick={() => onZakaznik(o.zakaznik.id)} className="text-xs font-semibold flex items-center gap-1" style={{ background: 'none', border: 'none', padding: 0, color: SECTION_COLOR, cursor: 'pointer' }}>
                                    <User size={12} />Karta hosta
                                </button>
                            )}
                        </div>

                        {(o?.polozky || []).length > 0 && (
                            <>
                                {nadpis('Co objednal')}
                                {o.polozky.map((p, i) => (
                                    <div key={i} className="flex justify-between gap-3 text-[13px]" style={{ padding: p.pridavek ? '2px 0 2px 16px' : '5px 0', color: p.pridavek ? muted : 'var(--text-primary)' }}>
                                        <span className="min-w-0 truncate">{p.pocet < 0 ? 'bez ' : `${p.pocet}× `}{p.nazev}</span>
                                        <span className="tabular-nums shrink-0">{p.celkem ? fmtKc(p.celkem) : ''}</span>
                                    </div>
                                ))}
                            </>
                        )}

                        {nadpis('Vyřízení')}
                        <div className="flex justify-between gap-4 text-[13.5px]" style={{ minHeight: 34, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                            <span style={{ color: muted }}>Pobočka</span>
                            {h.vyjadreni && VYJADRENI[h.vyjadreni]
                                ? <span className="text-right" style={{ color: VYJADRENI[h.vyjadreni].barva || 'var(--text-primary)', fontWeight: 600 }}>
                                    {VYJADRENI[h.vyjadreni].nazev.replace('Pobočka: ', '').replace('Pobočka ', '')}{h.vyjadreni_kde ? ` · ${h.vyjadreni_kde}` : ''} · {kdy(h.vyjadreni_v)}
                                </span>
                                : <span style={{ color: muted }}>zatím se nevyjádřila</span>}
                        </div>
                        {h.stav !== 'nove' && (
                            <>
                                {pole('Řešení', STAVY[h.stav]?.nazev ?? h.stav)}
                                {pole('Vyřešil', h.vyresil ? `${h.vyresil} · ${kdy(h.vyreseno_v)}` : kdy(h.vyreseno_v))}
                                {['penize', 'body'].includes(h.stav) && pole('Částka', fmtKc(h.castka))}
                                {['penize', 'body'].includes(h.stav) && pole('Provedeno v Objedname', h.provedeno_v ? `${h.provedl ?? ''} · ${kdy(h.provedeno_v)}` : 'zatím ne')}
                                {h.doplneni_id && pole('Doplnění', `objednávka ${h.doplneni_id}`)}
                            </>
                        )}
                        {h.odpovezeno_v && (
                            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                                <p className="text-xs" style={{ color: muted, margin: 0 }}>Odpověď {h.odpoved_kanal === 'email' ? 'e-mailem' : 'SMS'} · {h.odpovedel ?? ''} · {kdy(h.odpovezeno_v)}</p>
                                <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary)', margin: '4px 0 0' }}>{h.odpoved_text}</p>
                            </div>
                        )}

                        {muzeUpravit && chybiNove && (
                            <div style={{ marginTop: 6, border: `1px solid ${okraj}`, borderRadius: 12, padding: 14 }}>
                                <b className="text-sm" style={{ color: 'var(--text-primary)' }}>Řešení</b>
                                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', marginTop: 10 }}>
                                    {AKCE_CHYBI.map(a => (
                                        <span key={a.id} style={{ display: 'contents' }}>{volba(reseni === a.id, a.nazev, () => { setReseni(a.id); }, a.jenSPolozkami && !lzeDovezt)}</span>
                                    ))}
                                </div>
                                {['body', 'penize'].includes(reseni) && (
                                    <label className="flex items-center gap-2 text-xs" style={{ color: muted, marginTop: 10 }}>
                                        {reseni === 'body' ? 'Body za' : 'Vrátit'}
                                        <input value={castka} onChange={e => setCastka(e.target.value.replace(/[^0-9.,]/g, ''))} inputMode="decimal"
                                            placeholder="podle položek" style={{ ...inputStyle, width: 110, height: 36, fontSize: 14 }} />
                                        Kč
                                    </label>
                                )}
                                {!lzeDovezt && <p className="text-xs" style={{ color: muted, margin: '8px 0 0' }}>Dovézt jde jen u rozvozu s označenou položkou.</p>}
                            </div>
                        )}

                        {muzeUpravit && blokOdpovedi(h, { sOdeslanim: !chybiNove })}

                        {muzeUpravit && chybiNove && (
                            <div className="flex flex-wrap gap-2 justify-end" style={{ marginTop: 12 }}>
                                {tlacitko('jen', 'Vyřešit bez odpovědi', () => vyresit(reseni), { disabled: !reseni })}
                                {tlacitko('obe', `Vyřešit a odeslat ${kanal === 'sms' ? 'SMS' : 'e-mail'}`, vyresitAOdpovedet, { primarni: true, disabled: !reseni || !odpoved.trim() })}
                            </div>
                        )}

                        {h.druh !== 'chybi' && (h.odmena_body != null || muzeUpravit) && (
                            <div style={{ marginTop: 14, border: `1px solid ${okraj}`, borderRadius: 12, padding: 14 }}>
                                <b className="text-sm flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}><Gift size={15} />Odměna body</b>
                                {h.odmena_body != null ? (
                                    <>
                                        <p className="text-sm" style={{ color: 'var(--text-primary)', margin: '8px 0 0' }}>
                                            {Math.round(h.odmena_body)} bodů · {h.odmenil ?? ''} · {kdy(h.odmena_v)}
                                        </p>
                                        <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>
                                            {h.provedeno_v ? `Připsáno${h.provedl ? ` · ${h.provedl}` : ''} · ${kdy(h.provedeno_v)}` : 'Body připiš hostovi v Objedname (věrnostní program), pak to tady odškrtni.'}
                                        </p>
                                        {muzeUpravit && h.k_provedeni && (
                                            <div style={{ marginTop: 10 }}>{tlacitko('provedeno', <span className="flex items-center gap-1.5"><Check size={15} />Body připsány</span>, () => upravit('provedeno', { provedeno: true }), { primarni: true })}</div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 10 }}>
                                        {[20, 50, 100].map(n => (
                                            <span key={n} style={{ display: 'contents' }}>{volba(body === String(n), `${n} bodů`, () => setBody(String(n)))}</span>
                                        ))}
                                        <input value={body} onChange={e => setBody(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="jiný počet"
                                            aria-label="Počet bodů" style={{ ...inputStyle, width: 110, height: 40, fontSize: 14 }} />
                                        {tlacitko('odmenit', 'Odměnit', () => poslat('odmenit', `/api/objednavky/hlaseni/${h.id}/odmenit`, 'POST', { body: Number(body) }), { primarni: true, disabled: !(Number(body) > 0) })}
                                    </div>
                                )}
                            </div>
                        )}

                        {muzeUpravit && ['penize', 'body'].includes(h.stav) && (
                            <div style={{ marginTop: 14 }}>
                                <div className="flex flex-wrap items-end gap-2">
                                    <label className="text-xs" style={{ color: muted }}>
                                        Částka (Kč)
                                        <input value={castka} onChange={e => setCastka(e.target.value.replace(/[^0-9.,]/g, ''))} inputMode="decimal" style={{ ...inputStyle, width: 120, height: 38, fontSize: 14, marginTop: 4 }} />
                                    </label>
                                    {castka !== String(Math.round(h.castka)) && tlacitko('castka', 'Uložit částku', () => upravit('castka', { castka: castkaCislo ?? 0 }))}
                                    {h.k_provedeni && tlacitko('provedeno', <span className="flex items-center gap-1.5"><Check size={15} />{h.stav === 'body' ? 'Body připsány' : 'Peníze vráceny'}</span>,
                                        () => upravit('provedeno', { provedeno: true }), { primarni: true })}
                                    {h.provedeno_v && tlacitko('zpet', 'Ještě neprovedeno', () => upravit('zpet', { provedeno: false }))}
                                </div>
                                {h.k_provedeni && (
                                    <p className="text-xs" style={{ color: muted, marginTop: 8 }}>
                                        {h.stav === 'body' ? 'Body se připisují v Objedname (věrnostní program).' : o?.zaplaceno && platbaPopis(o?.typ_platby) === 'Online' ? 'Zaplaceno online — vrácení proveď v Objedname (refundace).' : 'Vrať hotově nebo na účet a zapiš do pokladní knihy.'}
                                        {' '}Pak to tady odškrtni.
                                    </p>
                                )}
                            </div>
                        )}

                        {muzeUpravit && (
                            <label className="block text-xs" style={{ color: muted, marginTop: 14 }}>
                                Interní poznámka
                                <textarea value={poznamka} onChange={e => setPoznamka(e.target.value)} rows={2} placeholder="Co jsme hostovi slíbili, s kým mluvil…"
                                    style={{ ...inputStyle, height: 'auto', padding: '8px 10px', fontSize: 14, marginTop: 4, resize: 'vertical' }} />
                                {h.stav !== 'nove' && (poznamka || '') !== (h.vyrizeni_poznamka || '') && (
                                    <span style={{ display: 'inline-block', marginTop: 6 }}>{tlacitko('poznamka', 'Uložit poznámku', () => upravit('poznamka', { vyrizeni_poznamka: poznamka.trim() || null }))}</span>
                                )}
                            </label>
                        )}
                        {!muzeUpravit && h.vyrizeni_poznamka && <p className="text-sm" style={{ color: 'var(--text-primary)', marginTop: 8 }}>{h.vyrizeni_poznamka}</p>}
                        {chyba && <p className="text-xs" style={{ color: CERVENA, marginTop: 8 }}>{chyba}</p>}
                        {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                    </div>
                )}
            </div>
        );
    };

    const panel = () => {
        const od = Date.now() - 30 * 86400000;
        const mesic = polozky.filter(h => new Date(h.vytvoreno_v).getTime() >= od);
        const oblasti = Object.entries(mesic.filter(h => h.druh === 'stiznost' && h.oblast).reduce((a, h) => ({ ...a, [h.oblast]: (a[h.oblast] || 0) + 1 }), {})).sort((a, b) => b[1] - a[1]);
        const radekP = (l, v, zvyr) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: zvyr ? SECTION_COLOR : 'var(--text-primary)' }}>{v}</b>
            </div>
        );
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' }}>Reklamace</p>
                {radekP('Čeká na vyřízení', polozky.filter(h => h.stav === 'nove').length, polozky.some(h => h.stav === 'nove'))}
                {radekP('Provést v Objedname', polozky.filter(h => h.k_provedeni).length, polozky.some(h => h.k_provedeni))}
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 8px' }}>Za 30 dní</p>
                {radekP('Pochvaly', mesic.filter(h => h.druh === 'pochvala').length)}
                {radekP('Stížnosti', mesic.filter(h => h.druh === 'stiznost').length)}
                {radekP('Něco chybí', mesic.filter(h => h.druh === 'chybi').length)}
                {oblasti[0] && radekP('Nejčastější stížnost', `${OBLASTI[oblasti[0][0]] ?? oblasti[0][0]} (${oblasti[0][1]}×)`)}
                {radekP('Vráceno', fmtKc(mesic.filter(h => h.stav === 'penize').reduce((s, h) => s + h.castka, 0)))}
                {radekP('Body (Kč)', fmtKc(mesic.filter(h => h.stav === 'body').reduce((s, h) => s + h.castka, 0)))}
                {radekP('Odměny za pochvaly a stížnosti', `${Math.round(mesic.reduce((s, h) => s + (h.odmena_body || 0), 0))} bodů`)}
                <p className="text-xs" style={{ color: muted, marginTop: 12 }}>
                    O vrácení peněz a bodech rozhoduje vedení tady. Pobočka jen doveze, potvrdí, jestli opravdu chybělo, stížnost vezme na vědomí a pochvalu přijme (host dostane poděkování).
                </p>
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranoId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
        </div>
    );
}
